import type { AutocompleteData } from "@color-finder/color-finder-api/models";
import ExpandMoreSharp from "@mui/icons-material/ExpandMoreSharp";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
} from "@mui/material";
import {
  ColorFilter,
  type ColorFilterValue,
} from "components/filter/color-filter/ColorFilter";
import { MaterialFilter } from "components/filter/material-filter/MaterialFilter";
import { useAuthUser } from "hooks/authentication";
import { type FilterEntry, useSingleFilter } from "hooks/filters";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { checkInternalUser } from "service/utils/get-user-type";
import { GeneralFilter } from "./general-filter/GeneralFilter";
import { OpticalMaterialDataFilter } from "./samples-filter/OpticalMaterialDataFilter";

export interface SearchFilterProps {
  columns?: number;
  childColumns?: number;
  defaultExpanded?: boolean;
  canMinimize?: boolean;
  value: Array<FilterEntry>;
  onChange: (filter: Array<FilterEntry>) => void;
  isLandingPage?: boolean;
}

const isMiscellaneousVisible = ({
  isInternal,
  isLandingPage,
  hasEmail,
}: {
  isInternal: boolean;
  isLandingPage: boolean;
  hasEmail: boolean;
}): boolean => isInternal && (!isLandingPage || hasEmail);

export const SearchFilter = ({
  value,
  onChange,
  columns = 1,
  childColumns = 12,
  defaultExpanded = true,
  canMinimize = true,
  isLandingPage = false,
}: SearchFilterProps) => {
  const [colorStatus, setColorStatus] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "colorStatus");
  const [substanceList, setSubstanceList] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "substanceList");
  const [cdc, setCdc] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "cdc",
  );
  const [oemName, setOemName] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "oemName",
  );
  const [customerName, setCustomerName] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "customerName");
  const [sapNumber, setSapNumber] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "uvp",
  );
  const [grade, setGrade] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "grade",
  );
  const [brand, setBrand] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "brand",
  );
  const [gradeNumber, setGradeNumber] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "gradeNumber");
  const [gradeProperty, setGradeProperty] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "gradeProperty");
  const [opacity, setOpacity] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "opacity",
  );
  const [colorEffect, setColorEffect] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "colorEffect");
  const [colorTag, setColorTag] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "colorTag",
  );
  const [colorGroup, setColorGroup] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "colorGroup",
  );
  const [color, setColor] = useSingleFilter<ColorFilterValue | undefined>(
    value,
    onChange,
    "color",
  );
  const [hasDigitalSamples, setHasDigitalSamples] = useSingleFilter<
    boolean | undefined
  >(value, onChange, "hasDigitalSample");
  const [hasLevel3Data, setHasLevel3Data] = useSingleFilter<
    boolean | undefined
  >(value, onChange, "hasLevel3Data");
  const [industry, setIndustry] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "industry",
  );
  const [applicationClass, setApplicationClass] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "applicationClass");
  const [application, setApplication] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "application");
  const [texture, setTexture] = useSingleFilter<Array<AutocompleteData>>(
    value,
    onChange,
    "texture",
  );
  const [isColorSystem, setIsColorSystem] = useSingleFilter<string | undefined>(
    value,
    onChange,
    "isColorSystem",
  );
  const [gradeShortName, setGradeShortName] = useSingleFilter<
    Array<AutocompleteData>
  >(value, onChange, "gradeShortName");
  const { user } = useAuthUser();
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const isInternal: boolean = checkInternalUser(user, settings);

  const expandIcon = canMinimize ? <ExpandMoreSharp /> : null;
  const titleFontWeight = isLandingPage ? 700 : 400;
  const landingColumns = isLandingPage && (!isInternal || !user);
  const { t } = useTranslation();

  return (
    <Grid
      container
      columns={{
        xs: 1,
        md: landingColumns ? 2 : columns,
        lg: landingColumns ? 3 : columns,
      }}
      spacing={isLandingPage ? 0 : 2}
      sx={{ "& .MuiPaper-root": { boxShadow: "none" } }}
    >
      <Grid size={1}>
        <Accordion sx={{ height: "100%" }} defaultExpanded={defaultExpanded}>
          <AccordionSummary
            style={{ opacity: 1 }}
            disabled={!canMinimize}
            expandIcon={expandIcon}
          >
            <Typography
              fontFamily='var(--font-family, "AktivGrotesk")'
              fontWeight={titleFontWeight}
            >
              {t("src_filter_block_color")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ColorFilter
              key={JSON.stringify(color)}
              value={color}
              onChange={setColor}
              colorEffectValue={colorEffect}
              onColorEffectValueChange={setColorEffect}
              opacityValue={opacity}
              onOpacityValueChange={setOpacity}
              colorTagValue={colorTag}
              onColorTagValueChange={setColorTag}
              colorGroupValue={colorGroup}
              onColorGroupValueChange={setColorGroup}
              columns={childColumns}
              isColorSystem={isColorSystem}
              setIsColorSystem={setIsColorSystem}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid size={1}>
        <Accordion sx={{ height: "100%" }} defaultExpanded={defaultExpanded}>
          <AccordionSummary
            style={{ opacity: 1 }}
            disabled={!canMinimize}
            expandIcon={expandIcon}
          >
            <Typography
              fontFamily='var(--font-family, "AktivGrotesk")'
              fontWeight={titleFontWeight}
              fontSize={"1rem"}
            >
              {t("src_filter_block_material")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <MaterialFilter
              filters={value}
              grades={grade}
              onGradesChange={setGrade}
              industry={industry}
              onIndustryChange={setIndustry}
              applicationClass={applicationClass}
              onApplicationClassChange={setApplicationClass}
              application={application}
              onApplicationChange={setApplication}
              brands={brand}
              onBrandsChange={setBrand}
              gradeProperties={gradeProperty}
              onGradePropertiesChange={setGradeProperty}
              gradeNumbers={gradeNumber}
              onGradeNumbersChange={setGradeNumber}
              columns={childColumns}
              isLandingPage={isLandingPage}
              settings={settings}
              gradeShortName={gradeShortName}
              onGradeShortNameChange={setGradeShortName}
              isInternal={isInternal}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid size={1}>
        <Accordion sx={{ height: "100%" }} defaultExpanded={defaultExpanded}>
          <AccordionSummary
            style={{ opacity: 1 }}
            disabled={!canMinimize}
            expandIcon={expandIcon}
          >
            <Typography
              fontFamily='var(--font-family, "AktivGrotesk")'
              fontWeight={titleFontWeight}
              fontSize={"1rem"}
            >
              {t("src_filter_block_finish")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <OpticalMaterialDataFilter
              hasDigitalSamplesValue={hasDigitalSamples}
              onHasDigitalSamplesValueChange={setHasDigitalSamples}
              hasLevel3Data={hasLevel3Data}
              onHasLevel3DataChange={setHasLevel3Data}
              columns={childColumns}
              isExternal={!isInternal}
              texture={texture}
              onTextureChange={setTexture}
              isLandingPage={isLandingPage}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      {isMiscellaneousVisible({
        isInternal,
        isLandingPage,
        hasEmail: Boolean(user?.email),
      }) && (
        <Grid size={1}>
          <Accordion sx={{ height: "100%" }} defaultExpanded={defaultExpanded}>
            <AccordionSummary
              style={{ opacity: 1 }}
              disabled={!canMinimize}
              expandIcon={expandIcon}
            >
              <Typography
                fontFamily='var(--font-family, "AktivGrotesk")'
                fontWeight={titleFontWeight}
                fontSize={"1rem"}
              >
                {t("src_filter_block_miscellaneous")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <GeneralFilter
                colorStatusValue={colorStatus}
                onColorStatusValueChange={setColorStatus}
                substanceListValue={substanceList}
                onSubstanceListValueChange={setSubstanceList}
                cdcs={cdc}
                onCdcsChange={setCdc}
                oemName={oemName}
                oemNameChange={setOemName}
                customerName={customerName}
                customerNameChange={setCustomerName}
                columns={childColumns}
                setSapNumber={setSapNumber}
                sapNumber={sapNumber}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
    </Grid>
  );
};
